/**
 * CR-6441: sectioned layout for the Purchasing → ASN filter panel.
 * CR-6449: Added missing fields from the design diagram:
 *   - Quick select date chips (Last 3 months, Last 12 months, Custom)
 *   - Warehouse dropdown in Company section
 *   - Supplier Country dropdown in Supplier section
 *   - Part number and Description text inputs in Order Details
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Date Range · Company · Supplier · Classification · Order Details.
 *
 * CR-6449: For Supplier View:
 *   - Collapsible accordion sections with chevrons and blue dot indicators
 *   - Header bar with: Clear all, Hide filters, Apply Filters
 *   - Additional fields: Quick select, Warehouse, Supplier Country, Part number, Description
 */
import { ChevronDown, X, Search, CalendarRange, Building2, Users, Layers, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass, CollapsibleSection } from '../../../filters/FilterSection';

interface FilterOption { value: string; label: string }

/** ASN status options */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'created', label: 'Created' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Courier options */
const COURIER_OPTIONS: FilterOption[] = [
  { value: 'fedex', label: 'FedEx' },
  { value: 'ups', label: 'UPS' },
  { value: 'dhl', label: 'DHL' },
  { value: 'tnt', label: 'TNT' },
  { value: 'dpd', label: 'DPD' },
  { value: 'other', label: 'Other' },
];

/** Courier delivery status options - matches backend ASN.courier_delivery_status choices */
const COURIER_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'exception', label: 'Exception' },
  { value: 'unknown', label: 'Unknown' },
];

export interface ASNFilterSectionsProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  siteOptions: FilterOption[];
  warehouseOptions: FilterOption[];
  supplierCountryOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  /** CR-6449: Currently selected quick date range chip value */
  quickSelectValue: string;
  onFilterChange: (key: string, value: string) => void;
  /** CR-6449: Quick select date range chip change callback */
  onQuickSelectChange: (value: string) => void;
  /** CR-6449: Apply filters callback */
  onApply?: () => void;
  /** CR-6449: Clear all filters callback */
  onClearFilters: () => void;
  /** CR-6449: Hide filters callback */
  onHideFilters?: () => void;
  /** CR-6449: Whether this is supplier view - controls which fields are shown */
  isSupplier?: boolean;
  /** CR-6441: Division options for cascade filtering */
  divisionOptions?: FilterOption[];
  /** CR-6441: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6160: Whether ASN logistics tracking module is enabled - gates courier status filter */
  trackingEnabled?: boolean;
}

export function ASNFilterSections({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  supplierCountryOptions,
  supplierGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  quickSelectValue,
  onFilterChange,
  onQuickSelectChange,
  onApply,
  onClearFilters,
  onHideFilters,
  isSupplier = false,
  divisionOptions = [],
  siteCountries = [],
  trackingEnabled = false,
}: ASNFilterSectionsProps) {
  // Check which sections have active filters for the blue dot indicator
  const hasDateFilters = !!filters['dateFrom'] || !!filters['dateTo'] || !!quickSelectValue;
  // CR-6441: Added division and companyCountry to company filters check
  const hasCompanyFilters = !!filters['site'] || !!filters['warehouse'] || !!filters['division'] || !!filters['companyCountry'];
  // CR-6441: Added supplierCode and strategic to supplier filters check
  const hasSupplierFilters = !!filters['supplierGroup'] || !!filters['supplier'] || !!filters['supplierCountry'] || !!filters['supplierCode'] || filters['strategic'] === 'yes';
  const hasClassificationFilters = !!filters['commodityGroup'] || !!filters['commodityType'];
  // CR-6441: Added buyerId to order filters check
  // CR-6160: Only include courierDeliveryStatus in filter check when tracking module is enabled
  const hasOrderFilters = !!textInputs['asnNumber'] || !!filters['status'] || !!textInputs['poNumber'] ||
    !!textInputs['poLineNumber'] || !!textInputs['partNumber'] || !!textInputs['description'] ||
    !!filters['courier'] || !!textInputs['trackingNumber'] || !!filters['buyerId'] ||
    (trackingEnabled && !!filters['courierDeliveryStatus']);

  /** CR-6441: Quick select chip styling - matches Dashboard design */
  const chipBaseCls = 'px-4 py-2 text-sm font-medium border rounded-md transition-colors';
  const chipActiveCls = 'border-primary bg-primary/10 text-primary hover:bg-primary/20';
  const chipInactiveCls = 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary';

  /** Reusable text input field - reduces duplicate code */
  const textField = (label: string, key: string, placeholder: string, useTextInputs = true) => (
    <FilterField label={label}>
      <input
        type="text"
        placeholder={placeholder}
        value={useTextInputs ? (textInputs[key] || '') : (filters[key] || '')}
        onChange={(e) => onFilterChange(key, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
        className={filterControlClass}
      />
    </FilterField>
  );

  /** Reusable date input field - reduces duplicate code */
  const dateField = (label: string, key: string) => (
    <FilterField label={label}>
      <input
        type="date"
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={filterControlClass}
      />
    </FilterField>
  );

  /** Reusable select field - reduces duplicate code */
  const selectField = (
    label: string, 
    key: string, 
    options: FilterOption[], 
    placeholder = 'All',
  ) => (
    <FilterField label={label}>
      <select
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={filterControlClass}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </FilterField>
  );

  /** Reusable searchable select field */
  const searchableSelectField = (label: string, key: string, options: FilterOption[], placeholder: string) => (
    <FilterField label={label}>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onFilterChange(key, val)}
        placeholder={placeholder}
      />
    </FilterField>
  );

  /** Quick select chip button */
  const QuickSelectChip = ({ value, label }: { value: string; label: string }) => (
    <button
      type="button"
      onClick={() => onQuickSelectChange(value)}
      className={`${chipBaseCls} ${quickSelectValue === value ? chipActiveCls : chipInactiveCls}`}
    >
      {label}
    </button>
  );

  // CR-6449: Supplier View gets the new collapsible accordion design
  if (isSupplier) {
    return (
      <div className="border rounded-lg bg-card">
        {/* CR-6449: Filter panel header bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground">ASN Filters</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onClearFilters}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
            {onHideFilters && (
              <>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={onHideFilters}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <ChevronDown className="h-3.5 w-3.5 rotate-180" />
                  Hide filters
                </button>
              </>
            )}
            <button
              onClick={onApply}
              className="ml-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-1.5"
            >
              <Search className="h-3.5 w-3.5" />
              Apply Filters
            </button>
          </div>
        </div>

        {/* DATE RANGE - quick select chips and date inputs */}
        <CollapsibleSection title="Date Range" isActive={hasDateFilters}>
          <div className="flex flex-wrap items-end gap-5">
            <div className="flex gap-3 pb-1">
              <QuickSelectChip value="3months" label="Last 3 months" />
              <QuickSelectChip value="12months" label="Last 12 months" />
              <QuickSelectChip value="custom" label="Custom" />
            </div>
            <div className="hidden sm:block h-14 w-0.5 bg-border/60 rounded-full self-center" />
            <div className="w-56">{dateField('Date From', 'dateFrom')}</div>
            <div className="w-56">{dateField('Date To', 'dateTo')}</div>
          </div>
        </CollapsibleSection>

        {/* COMPANY section - CR-6441: Reordered to Division, Country, Site, Warehouse per diagram */}
        <CollapsibleSection title="Company" isActive={hasCompanyFilters}>
          <FilterGrid>
            {selectField('Division', 'division', divisionOptions, 'All Divisions')}
            <FilterField label="Country">
              <select
                value={filters['companyCountry'] || ''}
                onChange={(e) => onFilterChange('companyCountry', e.target.value)}
                className={filterControlClass}
              >
                <option value="">All Countries</option>
                {siteCountries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FilterField>
            {searchableSelectField('Site', 'site', siteOptions, 'All sites')}
            {searchableSelectField('Warehouse', 'warehouse', warehouseOptions, 'All warehouses')}
          </FilterGrid>
        </CollapsibleSection>

        {/* SUPPLIER section - CR-6441: Added Code field per diagram */}
        <CollapsibleSection title="Supplier" isActive={hasSupplierFilters}>
          <FilterGrid>
            {searchableSelectField('Country', 'supplierCountry', supplierCountryOptions, 'All countries')}
            {searchableSelectField('Group', 'supplierGroup', supplierGroupOptions, 'All groups')}
            {textField('Site', 'supplier', 'Supplier site name...')}
            {textField('Code', 'supplierCode', 'Supplier code...', false)}
          </FilterGrid>
          <div className="mt-4">
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={filters['strategic'] === 'yes'}
                onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium">Strategic suppliers only</span>
            </label>
          </div>
        </CollapsibleSection>

        {/* CLASSIFICATION section */}
        <CollapsibleSection title="Classification" isActive={hasClassificationFilters}>
          <FilterGrid>
            {selectField('Commodity Group', 'commodityGroup', commodityGroupOptions, 'All commodity groups')}
            {selectField('Commodity Type', 'commodityType', commodityTypeOptions, 'All commodity types')}
          </FilterGrid>
        </CollapsibleSection>

        {/* ORDER DETAILS section - CR-6441: Structured in 3 rows per diagram */}
        <CollapsibleSection title="Order Details" isActive={hasOrderFilters}>
          <FilterGrid>
            {textField('ASN Number', 'asnNumber', 'Enter ASN number...')}
            {selectField('ASN Status', 'status', STATUS_OPTIONS, 'All statuses')}
            {textField('PO Number', 'poNumber', 'Enter PO number...')}
            {textField('PO Line Number', 'poLineNumber', 'Enter line number...')}
          </FilterGrid>
          <div className="mt-4">
            <FilterGrid>
              {textField('Buyer ID', 'buyerId', 'Enter buyer ID...', false)}
              {textField('Part Number', 'partNumber', 'Enter part number...')}
              {textField('Description', 'description', 'Enter description...')}
              {selectField('Courier', 'courier', COURIER_OPTIONS, 'All couriers')}
            </FilterGrid>
          </div>
          <div className="mt-4">
            <FilterGrid>              
              {textField('Tracking Number', 'trackingNumber', 'Enter tracking number...')}
              {/* CR-6160: Courier status filter only available when logistics tracking module is enabled */}
              {trackingEnabled && selectField('Courier Delivery Status', 'courierDeliveryStatus', COURIER_STATUS_OPTIONS, 'All statuses')}
            </FilterGrid>
          </div>
        </CollapsibleSection>
      </div>
    );
  }

  // Customer View - Matches Dashboard filter styling with shaded section headers
  return (
    <div className="bg-card">
      {/* DATE RANGE section - CR-6441: Layout matches Dashboard */}
      <FilterSection title="Date Range" icon={CalendarRange}>
        <div className="flex flex-wrap items-start justify-between gap-6 items-center">
          <div className="flex flex-wrap gap-2 max-w-2xl">
            <QuickSelectChip value="3months" label="Last 3 months" />
            <QuickSelectChip value="12months" label="Last 12 months" />
            <QuickSelectChip value="custom" label="Custom" />
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="w-44">
              <FilterField label="From date">
                <input
                  type="date"
                  value={filters['dateFrom'] || ''}
                  onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                  className={filterControlClass}
                />
              </FilterField>
            </div>
            <div className="w-44">
              <FilterField label="To date">
                <input
                  type="date"
                  value={filters['dateTo'] || ''}
                  onChange={(e) => onFilterChange('dateTo', e.target.value)}
                  className={filterControlClass}
                />
              </FilterField>
            </div>
          </div>
        </div>
      </FilterSection>

      {/* COMPANY section - CR-6441: Reordered to Division, Country, Site, Warehouse per diagram */}
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          {selectField('Division', 'division', divisionOptions, 'All divisions')}
          <FilterField label="Country">
            <select
              value={filters['companyCountry'] || ''}
              onChange={(e) => onFilterChange('companyCountry', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All countries</option>
              {siteCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          {searchableSelectField('Site', 'site', siteOptions, 'All sites')}
          {searchableSelectField('Warehouse', 'warehouse', warehouseOptions, 'All warehouses')}
        </FilterGrid>
      </FilterSection>

      {/* SUPPLIER section - CR-6441: Added Code field per diagram */}
      <FilterSection title="Supplier" icon={Users}>
        <FilterGrid>
          {searchableSelectField('Supplier group', 'supplierGroup', supplierGroupOptions, 'All supplier groups')}
          {searchableSelectField('Supplier country', 'supplierCountry', supplierCountryOptions, 'All countries')}
          {textField('Supplier site', 'supplier', 'All supplier sites')}
          {textField('Supplier code', 'supplierCode', 'Enter supplier code', false)}
        </FilterGrid>
        <label className="flex items-center gap-2 cursor-pointer mt-4 w-fit">
          <input
            type="checkbox"
            checked={filters['strategic'] === 'yes'}
            onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium">Strategic suppliers only</span>
        </label>
      </FilterSection>

      {/* CLASSIFICATION section */}
      <FilterSection title="Classification" icon={Layers}>
        <FilterGrid>
          {selectField('Commodity Group', 'commodityGroup', commodityGroupOptions, 'All commodity groups')}
          {selectField('Commodity Type', 'commodityType', commodityTypeOptions, 'All commodity types')}
        </FilterGrid>
      </FilterSection>

      {/* ORDER DETAILS section - CR-6441: Added Buyer ID, Part number, Description per diagram */}
      <FilterSection title="Order Details" icon={FileText}>
        <FilterGrid>
          {textField('ASN Number', 'asnNumber', 'Enter ASN number...')}
          {selectField('ASN Status', 'status', STATUS_OPTIONS, 'All statuses')}
          {textField('PO Number', 'poNumber', 'Enter PO number...')}
          {textField('PO Line Number', 'poLineNumber', 'Enter line number...')}
        </FilterGrid>
        <div className="mt-4">
          <FilterGrid>
            {textField('Buyer ID', 'buyerId', 'Enter buyer ID...', false)}
            {textField('Part Number', 'partNumber', 'Enter part number...')}
            {textField('Description', 'description', 'Enter description...')}
            {selectField('Courier', 'courier', COURIER_OPTIONS, 'All couriers')}
          </FilterGrid>
        </div>
        <div className="mt-4">
          <FilterGrid>            
            {textField('Tracking Number', 'trackingNumber', 'Enter tracking number...')}
            {/* CR-6160: Courier status filter only available when logistics tracking module is enabled */}
            {trackingEnabled && selectField('Courier Delivery Status', 'courierDeliveryStatus', COURIER_STATUS_OPTIONS, 'All statuses')}
          </FilterGrid>
        </div>
      </FilterSection>

      {/* Filter footer - Customer View */}
      <div className="border-t px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={onClearFilters}
            className="px-5 py-2.5 border border-border rounded-md hover:bg-muted flex items-center gap-2 font-medium transition-all"
          >
            <X className="h-4 w-4" />
            Clear all
          </button>
          <button
            onClick={onApply}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium shadow-sm transition-all flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}
